import React, { useState, useMemo, useRef, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    IconButton,
    Typography,
    Paper,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import { useLocation } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import MicroWellApi from "../../../api/MicroWellApi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ConfirmDialog from "../../Common/ConfirmDialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";

const MicroWellPredict = () => {

    const createEmptyRows = (count = 5) =>
        Array.from({ length: count }, () => ({ sampleId: "", alu: "", concentration: "", conditionCode: "" }));

    const [openValidationDialog, setOpenValidationDialog] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [rows, setRows] = useState(createEmptyRows(5));
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");
    const [showChart, setShowChart] = useState(false);

    const fileInputRef = useRef(null);
    const chartRef = useRef(null);
    const location = useLocation();

    // =========================
    // GET RUN ID
    // =========================
    const runId = useMemo(() => {
        const stateRunId = location?.state?.runId;
        if (stateRunId) {
            sessionStorage.setItem("microwellSelectedRunId", stateRunId);
            return stateRunId;
        }
        return sessionStorage.getItem("microwellSelectedRunId");
    }, [location]);

    // =========================
    // SELECTED RUN DATA
    // =========================
    const rawData = useMemo(() => {
        if (!runId) return {};
        try {
            const history = JSON.parse(
                sessionStorage.getItem("microwellCalibrationHistory") || "{}"
            );
            return history?.[runId] || {};
        } catch (err) {
            console.error("Failed to load microwell run", err);
            return {};
        }
    }, [runId]);

    // The rawResponse stored in the history entry contains all calibration
    // output fields needed to build the predict assay payload.
    const calibrationResponse = rawData?.rawResponse || rawData?.result || {};
    const description = rawData?.description || "";
    const calParameters = rawData?.parameters || {};

    const { callPredictApi, predictLoading, predictError } = MicroWellApi(
        {},
        [],
        {
            ...calibrationResponse,
            eAssayModelType: Number(calParameters?.eAssayModelType || 0),
            eMwMathModel: Number(calParameters?.eMwMathModel || 0),
            afCalConcentration: (rawData?.rows || []).map(r => Number(r.conc)).filter(v => !isNaN(v)),
            sNumberCalLevels: (rawData?.rows || []).length,
            sNumMGCCPoints: (calParameters?.afMGCCConcentration || []).length,
            afMGCCConcentration: calParameters?.afMGCCConcentration || [],
            afMGCCSignal: calParameters?.afMGCCSignal || [],
            afCalSignal: calibrationResponse?.afCalSignal || []
        }
    );

    // =========================
    // PARSE INPUT
    // =========================
    const parseInput = (text) => {
        try {
            setError("");
            if (!text || !text.trim()) {
                setRows(createEmptyRows(5));
                return;
            }

            const lines = text.split(/\r?\n/);
            const parsed = [];

            lines.forEach((line, index) => {
                if (!line.trim()) return;
                let parts = line.split("\t");
                if (parts.length < 2) parts = line.trim().split(/\s+/);

                if (
                    parts[0]?.toLowerCase().includes("sample") ||
                    parts[1]?.toLowerCase().includes("alu") ||
                    parts[1]?.toLowerCase().includes("response")
                ) return;

                let sampleId = parts[0]?.trim();
                let alu = parts[1]?.trim();

                if (parts.length === 1) { sampleId = ""; alu = parts[0]; }
                if (alu === undefined || alu === "") {
                    throw new Error(`Missing ALU value at line ${index + 1}`);
                }
                if (isNaN(Number(alu))) {
                    throw new Error(`Invalid ALU at line ${index + 1}: "${alu}"`);
                }

                parsed.push({ sampleId: sampleId || "", alu, concentration: "" });
            });

            setRows(parsed.length ? parsed : createEmptyRows(5));
        } catch (err) {
            setError(err.message);
            setRows(createEmptyRows(5));
        }
    };

    // =========================
    // FILE READ
    // =========================
    const handleFileRead = (file) => {
        if (!file) return;
        const fileName = file.name.toLowerCase();

        if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
                    const text = json.map(r => r.map(c => String(c ?? "").trim()).join("\t")).join("\n");
                    setInputValue(text);
                    parseInput(text);
                } catch (err) {
                    setError("Failed to read Excel file.");
                }
            };
            reader.readAsArrayBuffer(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => { setInputValue(e.target.result); parseInput(e.target.result); };
        reader.readAsText(file);
    };

    const handleFileUpload = (e) => { handleFileRead(e.target.files?.[0]); e.target.value = null; };
    const handleDrop = (e) => { e.preventDefault(); handleFileRead(e.dataTransfer.files?.[0]); };

    const handleRowChange = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    // =========================
    // PREDICT
    // =========================
    const handlePredict = async () => {
        try {
            const validRows = rows.filter(
                r => r.alu !== undefined && r.alu !== null && String(r.alu).trim() !== ""
            );
            if (validRows.length === 0) {
                setError("Please enter at least one ALU value.");
                return;
            }
            setError("");

            const result = await callPredictApi(validRows);
            if (result) {
                setRows(prev => prev.map((r, i) => ({
                    ...r,
                    concentration: result[i]?.concentration ?? r.concentration,
                    conditionCode: result[i]?.conditionCode ?? r.conditionCode ?? ""
                })));
                setShowChart(true);

                // persist predictions into session history
                const existing = JSON.parse(
                    sessionStorage.getItem("microwellCalibrationHistory") || "{}"
                );
                const updated = { ...existing, [runId]: { ...rawData, predictionRows: result } };
                sessionStorage.setItem("microwellCalibrationHistory", JSON.stringify(updated));
            }
        } catch (err) {
            console.error(err);
            setError("Prediction failed. Please try again.");
        }
    };

    const handleClear = () => { setRows(createEmptyRows(5)); setShowChart(false); setError(""); setInputValue(""); };
    const handleAddRow = () => setRows(prev => [...prev, { sampleId: "", alu: "", concentration: "" }]);
    const handleDeleteRow = (index) => {
        const updated = rows.filter((_, i) => i !== index);
        setRows(updated.length ? updated : createEmptyRows(1));
    };
    const handleResetRows = () => { setShowChart(false); setRows(createEmptyRows(5)); };

    // =========================
    // CHART OPTIONS
    // MicroWell: X-axis = concentration (back-calculated), Y-axis = ALU (signal)
    // =========================
    const chartOptions = useMemo(() => {
        const validRows = rows.filter(
            r => r.alu !== "" && r.concentration !== "" &&
                !isNaN(Number(r.alu)) && !isNaN(Number(r.concentration))
        );

        const data = validRows.map(r => ({
            x: Number(r.concentration),
            y: Number(r.alu),
            name: r.sampleId || ""
        }));

        return {
            chart: { type: "scatter", height: 350 },
            title: { text: "MicroWell Prediction Results" },
            xAxis: { title: { text: "Concentration" } },
            yAxis: { title: { text: "ALU (Signal)" } },
            tooltip: {
                formatter: function () {
                    return `<b>${this.point.name || "Sample"}</b><br/>Conc: ${this.x}<br/>ALU: ${this.y}`;
                }
            },
            series: [{
                name: "Predictions",
                color: "#774def",
                data,
                marker: { radius: 5 }
            }],
            credits: { enabled: false }
        };
    }, [rows]);

    // =========================
    // PDF EXPORT
    // =========================
    const handleExportPDF = async () => {
        const doc = new jsPDF();
        doc.text("MicroWell Prediction Report", 14, 10);
        doc.text(`Run: ${runId || "N/A"} | Description: ${description || "-"}`, 14, 18);
        doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 26);

        let y = 35;

        // Calibration result summary
        const result = rawData?.result || {};
        autoTable(doc, {
            startY: y,
            head: [["Calibration Result"]],
            body: [
                ["ABF Calibrated", result.bABFCalibrated ?? ""],
                ["Shape Factor", result.ShapeFactor ?? ""],
                ["Fit Factor", result.FitFactor ?? ""],
                ["P1", result.P1 ?? ""],
                ["P2", result.P2 ?? ""],
                ["P3", result.P3 ?? ""],
                ["P4", result.P4 ?? ""],
                ["P5", result.P5 ?? ""]
            ],
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        // Predictions table
        autoTable(doc, {
            startY: y,
            head: [["#", "Sample ID", "ALU (Input)", "Concentration", "Condition Code"]],
            body: rows.filter(r => r.alu !== "").map((r, i) => [
                i + 1,
                r.sampleId,
                r.alu,
                r.concentration,
                r.conditionCode || ""
            ]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        doc.save(`MicroWell_Prediction_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    // =========================
    // EXCEL EXPORT
    // =========================
    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "MMWS MicroWell";
        workbook.created = new Date();

        const sheet = workbook.addWorksheet("Prediction Report");

        const headerStyle = {
            font: { bold: true, color: { argb: "FFFFFF" }, size: 11 },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: "774DEF" } },
            alignment: { vertical: "middle", horizontal: "center" }
        };

        // Calibration info header
        sheet.addRow(["MicroWell Prediction Report"]).getCell(1).font = { bold: true, size: 14 };
        sheet.addRow(["Run", runId || "N/A", "Description", description]);
        sheet.addRow(["Exported", new Date().toLocaleString()]);
        sheet.addRow([]);

        // Cal result summary
        const result = rawData?.result || {};
        sheet.addRow(["Calibration Result"]).getCell(1).font = { bold: true };
        sheet.addRow(["ABF Calibrated", result.bABFCalibrated ?? "", "Shape Factor", result.ShapeFactor ?? "", "Fit Factor", result.FitFactor ?? ""]);
        sheet.addRow(["P1", result.P1 ?? "", "P2", result.P2 ?? "", "P3", result.P3 ?? "", "P4", result.P4 ?? "", "P5", result.P5 ?? ""]);
        sheet.addRow([]);

        // Predictions table
        const predHeaderRow = sheet.addRow(["#", "Sample ID", "ALU (Input)", "Concentration (Predicted)", "Condition Code"]);
        predHeaderRow.eachCell(cell => Object.assign(cell, headerStyle));

        rows.filter(r => r.alu !== "").forEach((r, i) => {
            sheet.addRow([i + 1, r.sampleId, r.alu, r.concentration, r.conditionCode || ""]);
        });

        sheet.columns.forEach(col => { col.width = 22; });

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `MicroWell_Prediction_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Run context banner */}
            {runId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Using calibration run <strong>{runId}</strong>
                    {description ? ` � ${description}` : ""}. Return to Calibration tab to change the active run.
                </Alert>
            )}
            {!runId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No calibration run selected. Go to the Calibration tab, run Calibrate, then click <em>Predict ?</em> on the result.
                </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

                {/* LEFT: input */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Paste or upload ALU values (one sample per line: SampleID  ALU)
                    </Typography>

                    <Box
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        sx={{
                            border: "2px dashed #ccc",
                            borderRadius: 2,
                            p: 1.5,
                            mb: 1.5,
                            textAlign: "center",
                            cursor: "pointer"
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <UploadFileIcon sx={{ color: "#774def" }} />
                        <Typography variant="body2" color="text.secondary">
                            Drag & drop file or click to upload (CSV, TXT, XLSX)
                        </Typography>
                    </Box>
                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv,.txt"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                    />

                    <TextField
                        multiline
                        minRows={4}
                        fullWidth
                        placeholder={"Sample1\t125.3\nSample2\t240.6\nSample3\t98.1"}
                        value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); parseInput(e.target.value); }}
                        size="small"
                        sx={{ mb: 1, fontFamily: "monospace" }}
                    />

                    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                    {predictError && <Alert severity="error" sx={{ mb: 1 }}>{predictError}</Alert>}

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={handlePredict}
                            disabled={predictLoading || !runId}
                            sx={{ backgroundColor: "#774def", "&:hover": { backgroundColor: "#5e3bc7" } }}
                        >
                            {predictLoading ? "Predicting..." : "Predict"}
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow}>
                            Add Row
                        </Button>
                        <Button size="small" variant="outlined" color="warning" startIcon={<ClearIcon />} onClick={handleClear}>
                            Clear
                        </Button>
                        <Button size="small" variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={() => setOpenResetDialog(true)}>
                            Reset
                        </Button>
                        {showChart && (
                            <>
                                <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}>
                                    Export PDF
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<GridOnIcon />} onClick={handleExportExcel}>
                                    Export Excel
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>

                {/* RIGHT: results table */}
                <Box sx={{ flex: 1.5, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Results
                    </Typography>
                    <Paper variant="outlined" sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#774def" }}>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }}>#</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }}>Sample ID</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }}>ALU (Input)</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }}>Concentration</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }}>Condition Code</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 600 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={row.sampleId}
                                                onChange={(e) => handleRowChange(i, "sampleId", e.target.value)}
                                                variant="standard"
                                                placeholder="ID"
                                                sx={{ width: 90 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={row.alu}
                                                onChange={(e) => handleRowChange(i, "alu", e.target.value)}
                                                variant="standard"
                                                type="number"
                                                placeholder="0"
                                                sx={{ width: 90 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={row.concentration}
                                                variant="standard"
                                                InputProps={{ readOnly: true }}
                                                placeholder="�"
                                                sx={{ width: 100 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: row.conditionCode && row.conditionCode !== "0" && row.conditionCode !== ""
                                                        ? "warning.main"
                                                        : "text.secondary",
                                                    fontFamily: "monospace"
                                                }}
                                            >
                                                {row.conditionCode || "�"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleDeleteRow(i)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            </Box>

            {/* Chart */}
            {showChart && (
                <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <div ref={chartRef}>
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </div>
                </Box>
            )}

            <ConfirmDialog
                open={openValidationDialog}
                title="Validation Error"
                message={validationMessage}
                onConfirm={() => setOpenValidationDialog(false)}
                onCancel={() => setOpenValidationDialog(false)}
            />

            <ConfirmDialog
                open={openResetDialog}
                title="Reset Prediction Rows"
                message="This will clear all prediction rows. Continue?"
                onConfirm={() => { handleResetRows(); setOpenResetDialog(false); }}
                onCancel={() => setOpenResetDialog(false)}
            />

            {/* Version / API footer */}
            <Divider sx={{ mt: 3 }} />
            <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "text.secondary", fontSize: 11 }}
            >
                Endpoint: POST /api/v1/microwell/predict
                {"\u2002\u2022\u2002"}
                Input (user): fALU per sample
                {"\u2002\u2022\u2002"}
                Input (from calibration): eMwMathModel, sMwCalParameterCount, fMwCalParameter1�5,
                afCalConcentration, afCalSignal, sNumberCalLevels, sNumMGCCPoints,
                afMGCCConcentration, afMGCCSignal, fCalSQCutOff, eAssayModelType
                {"\u2002\u2022\u2002"}
                Output: dConcentration, zConditionCode
            </Typography>
        </Box>
    );
};

export default MicroWellPredict;
